<template>
    <AppHead :title="program.name" />
    <div class="px-4 md:px-8" data-page="resources.program-page">
        <div
            class="md:hidden relative w-screen bg-white dark:bg-slate-800 overflow-hidden -ml-4 sm:-ml-6 -mr-4 sm:-mr-6 -mt-6"
        >
            <img
                src="/assets/vector-transfer.svg"
                alt=""
                class="absolute inset-0 w-full h-full object-cover opacity-20 dark:hidden pointer-events-none"
            />

            <div
                class="relative z-10 flex w-full px-4 pt-6 pb-16 flex-col items-stretch"
            >
                <ProgramHeader
                    :program="program"
                    :alertsFeatureEnabled="canManageBonusAlerts"
                    :bonusAlertEnabled="bonusAlertEnabled"
                    :bonusAlertLoading="bonusAlertLoading"
                    :openBonusAlert="openBonusAlert"
                    :email="userData?.email"
                    @go-back="goBack()"
                    @toggle-favorite="toggleFavorite()"
                    @toggle-bonus-alert="toggleBonusAlert($event)"
                    @update:openBonusAlert="openBonusAlert = $event"
                />

                <ProgramUserPoints :userPoints="userPoints" variant="mobile" />

                <div
                    class="mt-12 flex w-full h-16 sm:h-20 items-center justify-start"
                >
                    <img
                        v-if="program.program_image"
                        :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                        :alt="program.name"
                        class="object-contain w-auto h-full"
                    />
                    <div v-else class="text-slate-400 text-sm font-medium">
                        {{ program.name.slice(0, 12) }}
                    </div>
                </div>

                <ProgramDescription
                    :description="program.description"
                    :shortDescription="program.short_description"
                    variant="mobile"
                />

                <ProgramFacts :program="program" variant="mobile" />
            </div>
        </div>

        <div
            class="hidden md:block relative overflow-hidden bg-white dark:bg-slate-800 -mx-4 md:-mx-8 -mt-[72px] lg:-mt-8"
        >
            <img
                src="/assets/vector-transfer.svg"
                alt=""
                class="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-20 dark:hidden"
            />

            <div
                class="relative z-10 flex w-full pt-12 pb-24 flex-col items-start px-8 md:px-32 box-border"
            >
                <ProgramHeader
                    :program="program"
                    :alertsFeatureEnabled="canManageBonusAlerts"
                    :bonusAlertEnabled="bonusAlertEnabled"
                    :bonusAlertLoading="bonusAlertLoading"
                    :openBonusAlert="openBonusAlert"
                    :email="userData?.email"
                    @go-back="goBack()"
                    @toggle-favorite="toggleFavorite()"
                    @toggle-bonus-alert="toggleBonusAlert($event)"
                    @update:openBonusAlert="openBonusAlert = $event"
                />

                <ProgramUserPoints :userPoints="userPoints" variant="desktop" />

                <div class="flex pt-12 items-start gap-12 self-stretch">
                    <div class="flex flex-col items-start flex-1">
                        <ProgramDescription
                            :description="program.description"
                            :shortDescription="program.short_description"
                            variant="desktop"
                        />

                        <ProgramFacts :program="program" variant="desktop" />
                    </div>

                    <div
                        class="flex w-56 h-32 pb-9 flex-col justify-center items-center gap-2.5"
                    >
                        <img
                            v-if="program.program_image"
                            :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                            :alt="program.name"
                            class="object-contain w-full h-full"
                        />
                        <div
                            v-else
                            class="text-slate-400 text-sm font-medium text-center"
                        >
                            {{ program.name.slice(0, 12) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex px-4 md:px-24 py-12 md:py-24 flex-col items-start">
            <TransferBonusAlertBanner
                v-if="transferBonusAlertsEnabled && !isSubscribed"
                :programName="program.name"
                :bannerDismissed="bonusAlertBannerDismissed"
                :dismissUrl="bonusAlertBannerDismissUrl"
                class="mb-6 w-full self-stretch"
            />
            <div
                class="flex flex-col md:flex-row pb-4 items-start gap-6 md:gap-12 self-stretch"
            >
                <div
                    class="flex max-w-full flex-col items-start gap-3 flex-1 self-stretch"
                >
                    <div class="flex flex-col items-start gap-3 max-w-[720px]">
                        <h2
                            class="text-slate-800 dark:text-slate-100 text-3xl font-medium leading-9"
                        >
                            {{ headerTitle }}
                        </h2>
                        <p
                            class="text-slate-800 dark:text-slate-100 text-base font-normal leading-6"
                        >
                            {{ headerDescription }}
                        </p>
                    </div>
                </div>

                <div
                    class="flex w-[253px] h-[72px] md:w-[265px] md:h-[72px] items-center gap-0 md:gap-px flex-shrink-0 relative mt-4 md:mt-0"
                >
                    <div
                        :class="[
                            'flex p-4 md:p-5 justify-center items-center flex-1 h-full md:w-auto md:h-auto md:flex-1 md:self-stretch rounded-lg md:rounded-xl bg-white dark:bg-slate-800 shadow-sm',
                            isFlexibleCurrency ? 'flex-col gap-2.5' : 'gap-2',
                        ]"
                    >
                        <template v-if="showProgramImageFirst">
                            <img
                                v-if="program.program_image"
                                :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                                :alt="program.name"
                                class="object-contain w-full h-full"
                            />
                            <div
                                v-else
                                class="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center"
                            >
                                <span class="text-xs text-slate-500 dark:text-slate-400">
                                    {{ program.name.charAt(0) }}
                                </span>
                            </div>
                        </template>
                        <template v-else>
                            <Icon
                                icon="credit-card"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                    </div>

                    <div
                        :class="[
                            'flex p-4 md:p-5 justify-center items-center flex-1 h-full md:w-auto md:h-auto md:flex-1 md:self-stretch rounded-lg md:rounded-xl bg-white dark:bg-slate-800 shadow-sm',
                            isFlexibleCurrency ? 'gap-2' : 'flex-col gap-2.5',
                        ]"
                    >
                        <template v-if="showTransferIcons">
                            <Icon
                                icon="airplane"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                            <Icon icon="hotel" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                        </template>
                        <template v-else>
                            <img
                                v-if="program.program_image"
                                :src="`${cdnUrl}/${program.program_image.path}/_small/${program.program_image.file_name}`"
                                :alt="program.name"
                                class="object-contain w-full h-full"
                            />
                            <div
                                v-else
                                class="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center"
                            >
                                <span class="text-xs text-slate-500 dark:text-slate-400">
                                    {{ program.name.charAt(0) }}
                                </span>
                            </div>
                        </template>
                    </div>

                    <div
                        class="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 md:w-8 md:h-8 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm z-10"
                    >
                        <Icon
                            icon="arrow-right-alt"
                            class="w-4 h-4 md:w-4 md:h-4 text-slate-600 dark:text-slate-300"
                        />
                    </div>
                </div>
            </div>

            <div
                class="flex w-[992px] pt-0.5 items-start gap-8 border-b border-slate-200 dark:border-slate-700"
            >
                <button
                    @click="currentTab = 'current-rates'"
                    :class="[
                        'flex py-3.5 items-start gap-2 border-b-2 transition-colors cursor-pointer',
                        currentTab === 'current-rates'
                            ? 'border-orange text-slate-800 dark:text-slate-100'
                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200',
                    ]"
                >
                    <span class="text-sm font-medium leading-5">
                        Current rates
                    </span>
                </button>

                <button
                    @click="currentTab = 'transfer-bonus-history'"
                    :class="[
                        'flex py-3.5 items-start gap-2 border-b-2 transition-colors cursor-pointer',
                        currentTab === 'transfer-bonus-history'
                            ? 'border-orange text-slate-800 dark:text-slate-100'
                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200',
                    ]"
                >
                    <span class="text-sm font-medium leading-5">
                        Transfer bonus history
                    </span>
                </button>
            </div>

            <div
                class="lg:hidden mb-6 w-full self-stretch"
                v-if="
                    currentTab === 'transfer-bonus-history' ||
                    currentTab === 'current-rates'
                "
            >
                <div class="flex items-center justify-between w-full pt-6">
                    <button
                        type="button"
                        @click="isMobileFiltersOpen = true"
                        class="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900/40"
                    >
                        <Icon icon="filters" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Filters
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="activeFiltersCount > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ activeFiltersCount }}
                            </span>
                        </Transition>
                    </button>

                    <button
                        v-if="
                            canManageBonusAlerts &&
                            currentTab === 'current-rates'
                        "
                        @click="
                            openBonusAlert =
                                openBonusAlert === 'mobile' ? null : 'mobile'
                        "
                        class="bonus-alert-button flex h-11 px-4 items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 dark:bg-slate-900/40"
                    >
                        <Icon
                            :icon="
                                bonusAlertEnabled ? 'bell-added-color' : 'alert'
                            "
                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            {{
                                bonusAlertEnabled
                                    ? 'Bonus alert enabled'
                                    : 'Add bonus alert'
                            }}
                        </span>
                    </button>
                </div>
            </div>

            <TransferRatesMobileCards
                :currentTab="currentTab"
                :historyRows="historyRows"
                :visibleMobilePartners="visibleMobilePartners"
                :remainingMobilePartners="remainingMobilePartners"
                :mobilePartnersToggleLabel="mobilePartnersToggleLabel"
                :formatDate="formatDate"
                :formatDateShort="formatDateShort"
                :formatProgramType="formatProgramType"
                :formatTransferTimeText="formatTransferTimeText"
                :isImmediateTransfer="isImmediateTransfer"
                @open-calculator="openCalculator($event)"
                @toggle-mobile-expand="
                    mobilePartnersExpanded = !mobilePartnersExpanded
                "
                @update:openBonusAlert="openBonusAlert = $event"
            />

            <TransferRatesFilters
                :currentTab="currentTab"
                :selectedProgramTypes="selectedProgramTypes"
                :programTypeOptions="programTypeOptions"
                :selectedAddedToMyPoints="selectedAddedToMyPoints"
                :addedToMyPointsOptions="addedToMyPointsOptions"
                :isSingleUser="isSingleUser"
                :singleUserToggle="singleUserToggle"
                :selectedBonusPrograms="selectedBonusPrograms"
                :bonusProgramSearchQuery="bonusProgramSearchQuery"
                :filteredBonusProgramOptions="filteredBonusProgramOptions"
                :isAnyFilterApplied="isAnyFilterApplied"
                :alertsFeatureEnabled="canManageBonusAlerts"
                :bonusAlertEnabled="bonusAlertEnabled"
                :bonusAlertLoading="bonusAlertLoading"
                :openBonusAlert="openBonusAlert"
                :programName="program.name"
                :email="userData?.email"
                :toggleProgramType="toggleProgramType"
                :clearProgramTypeFilters="clearProgramTypeFilters"
                :toggleAddedToMyPointsSelection="toggleAddedToMyPointsSelection"
                :isAddedToMyPointsSelected="isAddedToMyPointsSelected"
                :clearAddedToMyPointsFilter="clearAddedToMyPointsFilter"
                :toggleBonusProgram="toggleBonusProgram"
                :isBonusProgramSelected="isBonusProgramSelected"
                :clearBonusProgramFilters="clearBonusProgramFilters"
                :clearAllFilters="clearAllFilters"
                @update:singleUserToggle="singleUserToggle = $event"
                @update:bonusProgramSearchQuery="
                    bonusProgramSearchQuery = $event
                "
                @update:openBonusAlert="openBonusAlert = $event"
                @toggle-bonus-alert="toggleBonusAlert($event)"
            />

            <TransferRatesTable
                :currentTab="currentTab"
                :tableRows="tableRows"
                :isFlexibleCurrency="isFlexibleCurrency"
                :isAnyFilterApplied="isAnyFilterApplied"
                :formatDate="formatDate"
                :formatDateShort="formatDateShort"
                :formatProgramType="formatProgramType"
                :formatTransferTimeText="formatTransferTimeText"
                :isImmediateTransfer="isImmediateTransfer"
                @open-calculator="openCalculator($event)"
            />

            <FeaturedArticles :articles="program.featured_articles" />

            <WalletCardsSection
                :userWalletCards="program.user_wallet_cards"
                :programName="program.name"
                @navigate-to-card="navigateToWalletCard($event)"
            />

            <BestCardsSection
                :visibleBestCards="visibleBestCards"
                :programName="program.name"
                :highlightOffer="highlightOffer"
                :getEffectiveEarnRates="getEffectiveEarnRates"
                :visibleEarnRates="visibleEarnRates"
                :hasMoreEarnRates="hasMoreEarnRates"
                :isEarnRatesExpanded="isEarnRatesExpanded"
                :toggleEarnRates="toggleEarnRates"
                :getBenefitIcons="getBenefitIcons"
                :isThoughtsLong="isThoughtsLong"
                :isThoughtsExpanded="isThoughtsExpanded"
                :toggleThoughts="toggleThoughts"
                @toggle-card-display="toggleUserCardDisplay($event)"
                @redirect-next-card="redirectToMyNextCard()"
            />
        </div>

        <MobileFiltersSidebar
            :isOpen="isMobileFiltersOpen"
            :currentTab="currentTab"
            :selectedProgramTypes="selectedProgramTypes"
            :programTypeOptions="programTypeOptions"
            :selectedAddedToMyPoints="selectedAddedToMyPoints"
            :addedToMyPointsOptions="addedToMyPointsOptions"
            :isSingleUser="isSingleUser"
            :singleUserToggle="singleUserToggle"
            :bonusProgramSearchQuery="bonusProgramSearchQuery"
            :filteredBonusProgramOptions="filteredBonusProgramOptions"
            :toggleProgramType="toggleProgramType"
            :clearProgramTypeFilters="clearProgramTypeFilters"
            :toggleAddedToMyPointsSelection="toggleAddedToMyPointsSelection"
            :isAddedToMyPointsSelected="isAddedToMyPointsSelected"
            :clearAddedToMyPointsFilter="clearAddedToMyPointsFilter"
            :toggleBonusProgram="toggleBonusProgram"
            :isBonusProgramSelected="isBonusProgramSelected"
            :clearBonusProgramFilters="clearBonusProgramFilters"
            :clearCurrentRatesFilters="clearCurrentRatesFilters"
            @close="isMobileFiltersOpen = false"
            @update:bonusProgramSearchQuery="bonusProgramSearchQuery = $event"
            @update:singleUserToggle="singleUserToggle = $event"
        />

        <TransferCalculatorModal
            :isOpen="calculatorModalOpen"
            :fromProgram="isFlexibleCurrency ? program : selectedPartnerProgram"
            :toProgram="isFlexibleCurrency ? selectedPartnerProgram : program"
            :transferPartner="selectedPartner"
            @close="closeCalculator"
        />

        <BonusAlertSheet
            :isOpen="openBonusAlert === 'mobile'"
            :programName="program.name"
            :email="userData?.email"
            :bonusAlertEnabled="bonusAlertEnabled"
            :bonusAlertLoading="bonusAlertLoading"
            @close="openBonusAlert = null"
            @toggle="toggleBonusAlert($event)"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import TransferCalculatorModal from '@/components/ui/TransferCalculatorModal.vue';

// Composables
import { useBonusAlert } from '@/composables/useBonusAlert';
import { useDragScroll } from '@/composables/useDragScroll';
import { useProgramFilters } from '@/composables/useProgramFilters';
import { useSubscription } from '@/composables/useSubscription';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

// Sub-components
import ProgramHeader from './ProgramPage/ProgramHeader.vue';
import ProgramUserPoints from './ProgramPage/ProgramUserPoints.vue';
import ProgramDescription from './ProgramPage/ProgramDescription.vue';
import ProgramFacts from './ProgramPage/ProgramFacts.vue';
import TransferRatesFilters from './ProgramPage/TransferRatesFilters.vue';
import TransferRatesTable from './ProgramPage/TransferRatesTable.vue';
import TransferRatesMobileCards from './ProgramPage/TransferRatesMobileCards.vue';
import FeaturedArticles from './ProgramPage/FeaturedArticles.vue';
import WalletCardsSection from './ProgramPage/WalletCardsSection.vue';
import BestCardsSection from './ProgramPage/BestCardsSection.vue';
import MobileFiltersSidebar from './ProgramPage/MobileFiltersSidebar.vue';
import BonusAlertSheet from './ProgramPage/BonusAlertSheet.vue';
import TransferBonusAlertBanner from './ProgramPage/TransferBonusAlertBanner.vue';

const props = defineProps({
    program: Object,
    userPrograms: Array,
    bonusAlertEnabled: Boolean,
    bonusAlertBannerDismissed: Boolean,
    bonusAlertBannerDismissUrl: String,
    features: Object,
});

// --- Composables ---
const { isSubscribed } = useSubscription();
const { transferBonusAlertsEnabled } = useFeatureFlags();

const {
    bonusAlertLoading,
    bonusAlertEnabled,
    openBonusAlert,
    canManageBonusAlerts,
    showBonusAlertUpsell,
    toggleBonusAlert,
    handleFlashMessages,
} = useBonusAlert(props);

useDragScroll();

const currentTab = ref('current-rates');

const {
    selectedProgramTypes,
    selectedAddedToMyPoints,
    singleUserToggle,
    selectedBonusPrograms,
    bonusProgramSearchQuery,
    programTypeOptions,
    isSingleUser,
    addedToMyPointsOptions,
    filteredBonusProgramOptions,
    isAnyFilterApplied,
    activeFiltersCount,
    filteredTransferPartners,
    historyRows,
    tableRows,
    toggleProgramType,
    clearProgramTypeFilters,
    toggleAddedToMyPointsSelection,
    isAddedToMyPointsSelected,
    toggleBonusProgram,
    isBonusProgramSelected,
    clearBonusProgramFilters,
    clearCurrentRatesFilters,
    clearAddedToMyPointsFilter,
    clearAllFilters,
} = useProgramFilters(props, currentTab);

// --- Remaining local state ---
const calculatorModalOpen = ref(false);
const selectedPartner = ref(null);
const isMobileFiltersOpen = ref(false);

const userData = computed(() => usePage().props.userData);

const handleClickOutside = (event) => {
    const dropdown = event.target.closest('.bonus-alert-dropdown');
    const button = event.target.closest('.bonus-alert-button');

    if (!dropdown && !button && openBonusAlert.value !== null) {
        openBonusAlert.value = null;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    handleFlashMessages();
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

const isFlexibleCurrency = computed(
    () => props.program.type === 'flexible_currency'
);

const cdnUrl = window.cdnUrl;
const showProgramImageFirst = computed(() => isFlexibleCurrency.value);
const showTransferIcons = computed(() => isFlexibleCurrency.value);

const headerTitle = computed(() => {
    const programName = props.program?.name || '';
    const partnerName = selectedPartner.value?.destination_program_name;

    if (partnerName) {
        if (isFlexibleCurrency.value) {
            return `Transfer points to ${partnerName}`;
        }
        return `Transfer your ${partnerName} to ${programName}`;
    }

    if (isFlexibleCurrency.value) {
        return `Transfer your ${programName}`;
    }

    return `Transfer points to ${programName}`;
});

const headerDescription = computed(() => {
    const copyByType = {
        airline: (name) =>
            `Exchange rewards from your credit cards to ${name}'s loyalty program for flight redemptions and upgrades.`,
        hotel: (name) =>
            `Exchange credit card points to ${name} program to unlock free hotel stays, room upgrades, and elite benefits.`,
    };

    const partnerName = selectedPartner.value?.destination_program_name;
    const partnerType = selectedPartner.value?.destination_program_type;
    const type = props.program?.type;
    const programName = props.program?.name || '';

    if (partnerName) {
        if (isFlexibleCurrency.value) {
            if (partnerType === 'airline' || partnerType === 'hotel') {
                return copyByType[partnerType](partnerName);
            }
            return 'Exchange your points with the best partners for more value.';
        }
        if (type === 'airline' || type === 'hotel') {
            return copyByType[type](programName);
        }
        return 'Exchange your points with the best partners for more value.';
    }

    if (type === 'flexible_currency') {
        return 'Transfer your points to top airline and hotel loyalty programs for more value.';
    }
    if (type === 'airline' || type === 'hotel') {
        return copyByType[type](programName);
    }

    return 'Exchange your points for more value across top loyalty programs.';
});

const showAllCards = ref({});
const expandedThoughts = ref({});

const programCards = computed(() => props.program.program_cards || []);
const userPoints = computed(() => props.program.user_points || []);

const visibleBestCards = computed(() => {
    if (!programCards.value || programCards.value.length === 0) return [];

    return programCards.value.map((userGroup) => {
        const userShowAll = showAllCards.value[userGroup.user_id] || false;
        return {
            ...userGroup,
            cards: userShowAll ? userGroup.cards : userGroup.cards.slice(0, 2),
            showingAll: userShowAll,
            hasMoreCards: userGroup.cards.length >= 2 && !userShowAll,
            hasLessCards: userGroup.cards.length >= 2 && userShowAll,
        };
    });
});

const toggleUserCardDisplay = (userId) => {
    showAllCards.value[userId] = !showAllCards.value[userId];
};

const isThoughtsLong = (thoughts) => {
    if (!thoughts) return false;
    return thoughts.length > 150;
};

const isThoughtsExpanded = (cardId) => {
    return expandedThoughts.value[cardId] || false;
};

const toggleThoughts = (cardId) => {
    expandedThoughts.value[cardId] = !expandedThoughts.value[cardId];
};

const highlightOffer = (offer) => {
    if (!offer || typeof offer !== 'string') return '';
    const escapeHtml = (s) =>
        s
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\"/g, '&quot;')
            .replace(/'/g, '&#39;');
    const escaped = escapeHtml(offer);
    const pattern =
        /(\$[\d,]+(?:\.\d+)?|\d+(?:,\d{3})*(?:\.\d+)?%|\b\d{1,3}(?:,\d{3})+(?:\.\d+)?\b)/g;
    return escaped.replace(pattern, '<span class="text-orange">$1</span>');
};

const earnRatesExpanded = ref({});

const isEarnRatesExpanded = (cardId) => {
    return !!earnRatesExpanded.value[cardId];
};

const toggleEarnRates = (cardId) => {
    earnRatesExpanded.value[cardId] = !earnRatesExpanded.value[cardId];
};

const getEffectiveEarnRates = (card) => {
    if (!card || !Array.isArray(card.earnRates)) return [];
    return card.earnRates.filter((r) => {
        const m = (r?.multiplier ?? '').toString().trim();
        const c = (r?.category ?? '').toString().trim();
        return m !== '' || c !== '';
    });
};

const hasMoreEarnRates = (card) => {
    return getEffectiveEarnRates(card).length > 2;
};

const visibleEarnRates = (card) => {
    const rates = getEffectiveEarnRates(card);
    if (!rates.length) return [];
    return isEarnRatesExpanded(card.id) ? rates : rates.slice(0, 2);
};

const getBenefitIcons = (card) => {
    if (!card || !Array.isArray(card.benefits)) return [];
    const icons = card.benefits
        .map((b) => {
            if (!b) return null;
            if (typeof b === 'string') return b;
            if (
                typeof b === 'object' &&
                typeof b.icon === 'string' &&
                b.icon.trim() !== ''
            ) {
                return b.icon.trim();
            }
            return null;
        })
        .filter((v) => !!v);
    const seen = new Set();
    return icons.filter((v) => (seen.has(v) ? false : (seen.add(v), true)));
};

const goBack = () => {
    router.get('/resources/transfer-partners-guide');
};

const navigateToWalletCard = (card) => {
    // card_id must be the connected account id — the wallet matches it against
    // account.id when deciding which card's sidebar to open.
    if (!card.connected_account_id) {
        router.get('/wallet');
        return;
    }

    router.get('/wallet', {
        card_id: card.connected_account_id,
        open_sidebar: true,
    });
};

const redirectToMyNextCard = () => {
    router.get('/mynextcard');
};

const toggleFavoriteInProgress = ref(false);

const toggleFavorite = async () => {
    if (!props.program || !props.program.id || !props.program.slug) {
        return;
    }

    if (toggleFavoriteInProgress.value) {
        return;
    }
    toggleFavoriteInProgress.value = true;

    const wasAlreadyFavorite = props.program.is_favorite;

    router.post(
        `/resources/transfer-partners-guide/${props.program.slug}/toggle-favorite`,
        {},
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => {
                props.program.is_favorite = !props.program.is_favorite;
            },
            onSuccess: () => {
                toggleFavoriteInProgress.value = false;
                if (wasAlreadyFavorite) {
                    notify(
                        {
                            group: 'success',
                            title: 'Removed from favorites',
                            text: 'Program has been removed from favorites.',
                        },
                        4000
                    );
                } else {
                    notify(
                        {
                            group: 'success',
                            title: 'Added to favorites',
                            text: 'Program has been added to favorites.',
                        },
                        4000
                    );
                }
            },
            onError: () => {
                toggleFavoriteInProgress.value = false;
                props.program.is_favorite = !props.program.is_favorite;
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to update favorites. Please try again.',
                    },
                    4000
                );
            },
        }
    );
};

const parseLocalDateOnly = (s) => {
    if (!s || typeof s !== 'string') return null;
    const parts = s
        .substring(0, 10)
        .split('-')
        .map((n) => parseInt(n, 10));
    const [y, m, d] = [parts[0], parts[1], parts[2]];
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const formatDate = (dateString) => {
    const date = parseLocalDateOnly(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatDateShort = (dateString) => {
    const date = parseLocalDateOnly(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
};

const formatProgramType = (type) => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
};

const formatTransferTimeText = (value) => {
    const raw = value == null ? '' : String(value).trim();
    if (raw === '') return '--';
    if (raw === '0') return 'Immediate';

    const normalized = raw.replace(/\s*–\s*/g, '-');
    const range = normalized.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
        const a = parseInt(range[1], 10);
        const b = parseInt(range[2], 10);
        return a === b
            ? `${a} ${a === 1 ? 'day' : 'days'}`
            : `${a} – ${b} days`;
    }

    if (/^\d+$/.test(normalized)) {
        const n = parseInt(normalized, 10);
        return `${n} ${n === 1 ? 'day' : 'days'}`;
    }

    return raw;
};

const isImmediateTransfer = (value) => {
    if (value === null || value === undefined) return false;
    const raw = String(value).trim().toLowerCase();
    if (raw === '') return false;
    return (
        raw === '0' ||
        raw === 'instant' ||
        raw === 'immediate' ||
        raw === '0 day' ||
        raw === '0 days'
    );
};

const selectedPartnerProgram = computed(() => {
    if (!selectedPartner.value) return {};
    return {
        id: selectedPartner.value.destination_program_id,
        name: selectedPartner.value.destination_program_name,
        program_image: selectedPartner.value.destination_program_image || null,
        type: selectedPartner.value.destination_program_type,
    };
});

const openCalculator = (partner) => {
    selectedPartner.value = partner;
    calculatorModalOpen.value = true;
};

const mobilePartnersExpanded = ref(false);
const visibleMobilePartners = computed(() => {
    const list = filteredTransferPartners.value || [];
    return mobilePartnersExpanded.value ? list : list.slice(0, 8);
});

const remainingMobilePartners = computed(() => {
    const total = filteredTransferPartners.value?.length || 0;
    return Math.max(total - 8, 0);
});

const mobilePartnersToggleLabel = computed(() => {
    return mobilePartnersExpanded.value ? 'Show less' : `Show more`;
});

watch([filteredTransferPartners, currentTab], () => {
    mobilePartnersExpanded.value = false;
});

const closeCalculator = () => {
    calculatorModalOpen.value = false;
    selectedPartner.value = null;
};
</script>

<style scoped>
.slider {
    border-radius: 25px;

    &:before {
        position: absolute;
        content: '';
        height: 19px;
        width: 19px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
}

/* Only apply transition after user interaction */
input:focus + .slider:before,
input:active + .slider:before,
.switch:hover .slider:before {
    transition: 0.3s ease-in-out;
}

input:checked + .slider {
    background-color: #fd421d;

    &:before {
        transform: translateX(17px);
    }
}

:deep(.expanded-description),
:deep(.expanded-description *) {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    display: revert !important;
    line-clamp: unset !important;
    -webkit-line-clamp: unset !important;
    -webkit-box-orient: unset !important;
}

:deep([data-horizontal-scroll]) {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
    touch-action: pan-x;
}

:deep([data-horizontal-scroll].dragging) {
    scroll-snap-type: none !important;
    cursor: grabbing;
}

:deep([data-horizontal-scroll] a) {
    -webkit-user-drag: none;
    -webkit-touch-callout: none;
    user-select: none;
    touch-action: pan-x;
}

:deep([data-horizontal-scroll] img) {
    -webkit-user-drag: none;
    -webkit-touch-callout: none;
    user-select: none;
}
</style>
